import { api, unwrap } from '../../api/client';
import type {
  LanguageProficiency,
  PersonCertificate,
  PersonLicence,
  PersonMedical,
  PersonRating,
} from '../../api/types';
import type { DocumentKind } from './documentKinds';

/**
 * CRUD for the five document kinds.
 *
 * The typed client needs literal paths, so each kind is spelled out here once and every screen
 * works against `DocumentRecord` instead of five near-identical shapes.
 */
export type DocumentRecord = {
  id: number;
  person: number;
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
  is_suspended?: boolean;
  warning_days?: number;
  days_remaining?: number | null;
  validity_status?: string;
  notes?: string | null;
  scan?: string | null;
  /** The taxonomy row this document points at, absent for a language assessment. */
  type_id?: number | null;
  /** What to print as the document's name. */
  label: string;
  code?: string | null;
  reference?: string | null;
  issuer?: string | null;
  issued_on?: string | null;
  /** Only for ratings, which are the documents that get revalidated. */
  revalidated_on?: string | null;
  /** Only for a language assessment. */
  level?: number | null;
  language?: string | null;
  allows_ctr_entry?: boolean;
  limitations?: string | null;
};

export type DocumentPayload = Record<string, unknown>;

export async function listDocuments(
  kind: DocumentKind,
  personId: number,
): Promise<DocumentRecord[]> {
  const query = { person: personId, page_size: 100 } as const;
  switch (kind) {
    case 'licence': {
      const data = unwrap(await api.GET('/api/v1/licences/', { params: { query } }));
      return data.results.map(fromLicence);
    }
    case 'rating': {
      const data = unwrap(await api.GET('/api/v1/ratings/', { params: { query } }));
      return data.results.map(fromRating);
    }
    case 'medical': {
      const data = unwrap(await api.GET('/api/v1/medicals/', { params: { query } }));
      return data.results.map(fromMedical);
    }
    case 'language': {
      const data = unwrap(
        await api.GET('/api/v1/language-proficiencies/', { params: { query } }),
      );
      return data.results.map(fromLanguage);
    }
    case 'certificate': {
      const data = unwrap(await api.GET('/api/v1/certificates/', { params: { query } }));
      return data.results.map(fromCertificate);
    }
  }
}

/** Returns the saved record's id, which the caller needs to attach a scan afterwards. */
export async function saveDocument(
  kind: DocumentKind,
  id: number | null,
  body: DocumentPayload,
): Promise<number> {
  switch (kind) {
    case 'licence':
      return id
        ? unwrap(
            await api.PATCH('/api/v1/licences/{id}/', {
              params: { path: { id } },
              body: body as never,
            }),
          ).id
        : unwrap(await api.POST('/api/v1/licences/', { body: body as never })).id;
    case 'rating':
      return id
        ? unwrap(
            await api.PATCH('/api/v1/ratings/{id}/', {
              params: { path: { id } },
              body: body as never,
            }),
          ).id
        : unwrap(await api.POST('/api/v1/ratings/', { body: body as never })).id;
    case 'medical':
      return id
        ? unwrap(
            await api.PATCH('/api/v1/medicals/{id}/', {
              params: { path: { id } },
              body: body as never,
            }),
          ).id
        : unwrap(await api.POST('/api/v1/medicals/', { body: body as never })).id;
    case 'language':
      return id
        ? unwrap(
            await api.PATCH('/api/v1/language-proficiencies/{id}/', {
              params: { path: { id } },
              body: body as never,
            }),
          ).id
        : unwrap(await api.POST('/api/v1/language-proficiencies/', { body: body as never })).id;
    case 'certificate':
      return id
        ? unwrap(
            await api.PATCH('/api/v1/certificates/{id}/', {
              params: { path: { id } },
              body: body as never,
            }),
          ).id
        : unwrap(await api.POST('/api/v1/certificates/', { body: body as never })).id;
  }
}

export async function deleteDocument(kind: DocumentKind, id: number): Promise<void> {
  const params = { path: { id } } as const;
  switch (kind) {
    case 'licence':
      unwrap(await api.DELETE('/api/v1/licences/{id}/', { params }));
      return;
    case 'rating':
      unwrap(await api.DELETE('/api/v1/ratings/{id}/', { params }));
      return;
    case 'medical':
      unwrap(await api.DELETE('/api/v1/medicals/{id}/', { params }));
      return;
    case 'language':
      unwrap(await api.DELETE('/api/v1/language-proficiencies/{id}/', { params }));
      return;
    case 'certificate':
      unwrap(await api.DELETE('/api/v1/certificates/{id}/', { params }));
      return;
  }
}

/** Ratings are the only documents with a revalidation action, which is a rating-only concept. */
export async function revalidateRating(id: number, body: { revalidated_on?: string | null }) {
  return unwrap(
    await api.POST('/api/v1/ratings/{id}/revalidate/', {
      params: { path: { id } },
      body: body as never,
    }),
  );
}

const validity = (row: {
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
  is_suspended?: boolean;
  warning_days?: number;
  days_remaining?: number | null;
  validity_status?: string;
  notes?: string | null;
  scan?: string | null;
}) => ({
  valid_from: row.valid_from,
  valid_until: row.valid_until,
  is_active: row.is_active,
  is_suspended: row.is_suspended,
  warning_days: row.warning_days,
  days_remaining: row.days_remaining,
  validity_status: row.validity_status,
  notes: row.notes,
  scan: row.scan,
});

function fromLicence(row: PersonLicence): DocumentRecord {
  return {
    ...validity(row),
    id: row.id,
    person: row.person,
    type_id: row.licence_type,
    label: row.licence_name,
    code: row.licence_code,
    reference: row.licence_number,
    issuer: row.issuing_authority,
    issued_on: row.issued_on,
  };
}

function fromRating(row: PersonRating): DocumentRecord {
  return {
    ...validity(row),
    id: row.id,
    person: row.person,
    type_id: row.rating_type,
    label: row.rating_name,
    code: row.rating_code,
    issued_on: row.issued_on,
    revalidated_on: row.revalidated_on,
  };
}

function fromMedical(row: PersonMedical): DocumentRecord {
  return {
    ...validity(row),
    id: row.id,
    person: row.person,
    type_id: row.medical_class,
    label: row.medical_class_name,
    code: row.medical_class_code,
    reference: row.certificate_number,
    issuer: row.examiner_name,
    issued_on: row.examination_date,
    limitations: row.limitations,
  };
}

function fromLanguage(row: LanguageProficiency): DocumentRecord {
  return {
    ...validity(row),
    id: row.id,
    person: row.person,
    label: `${row.language} · ICAO ${row.level}`,
    language: row.language,
    level: row.level,
    issuer: row.assessment_body,
    issued_on: row.assessed_on,
    allows_ctr_entry: row.allows_ctr_entry,
  };
}

function fromCertificate(row: PersonCertificate): DocumentRecord {
  return {
    ...validity(row),
    id: row.id,
    person: row.person,
    type_id: row.certificate_type,
    label: row.certificate_name,
    code: row.certificate_code,
    reference: row.reference,
    issuer: row.issuing_body,
    issued_on: row.completed_on,
  };
}
